// src/components/admin/AdminHeader.jsx
import React from "react";
import {
  Layout,
  Menu,
  Avatar,
  Typography,
  Row,
  Col,
  Dropdown,
  Space,
} from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  BarChartOutlined,
  CalendarOutlined,
  SolutionOutlined,
   TeamOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";

const { Header } = Layout;
const { Text } = Typography;

/**
 * AdminHeader
 * - Shows logo/title on left
 * - Center navigation (Dashboard, Insights, Events, Jobs, Approvals, Settings)
 * - Right-side user dropdown (Profile Settings / Logout) with black text for dropdown items
 *
 * Header only renders when admin_token exists in localStorage.
 */
export default function AdminHeader() {
  const navigate = useNavigate();
  const location = useLocation();

  const adminName = localStorage.getItem("admin_name") || "Admin";
  const adminToken = localStorage.getItem("admin_token");

  // If not logged in, do not render header
  if (!adminToken) return null;

  // Small helper: get initials from name
  const getInitials = (name) => {
    if (!name) return null;
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
  };

  const initials = getInitials(adminName);

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    navigate("/admin/login", { replace: true });
  };

  // Build user dropdown menu with black text
  const userMenu = (
    <Menu
      selectable={false}
      style={{ minWidth: 180, borderRadius: 8, background: "#fff", color: "#000" }}
    >
      <Menu.Item
        key="profile"
        icon={<UserOutlined />}
        onClick={() => navigate("/admin/settings", { replace: false })}
        style={{ color: "#000" }}
      >
        Profile Settings
      </Menu.Item>

      <Menu.Divider />

      <Menu.Item
        key="logout"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        style={{ color: "#000" }}
      >
        Logout
      </Menu.Item>
    </Menu>
  );

  // Determine active menu key from current path
  const pathname = (location?.pathname || "").toLowerCase();
  let activeKey = "dashboard";
  if (pathname.includes("/admin/insights")) activeKey = "insights";
  else if (pathname.includes("/admin/events")) activeKey = "events";
  else if (pathname.includes("/admin/jobs")) activeKey = "jobs";
  else if (pathname.includes("/admin/approvals")) activeKey = "approvals";
  else if (pathname.includes("/admin/settings")) activeKey = "settings";
  else if (pathname.includes("/admin/dashboard")) activeKey = "dashboard";
  else if (pathname.includes("/admin/alumni-list")) activeKey = "alumni-list";

  return (
    <Header
      style={{
        background: "#000",
        padding: "0 28px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      <Row align="middle" justify="space-between" wrap={false}>
        {/* Left: Logo + Title */}
        <Col flex="none">
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate("/admin/dashboard")}
            onKeyDown={(e) => e.key === "Enter" && navigate("/admin/dashboard")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
            }}
          >
            <img
              src="/logo192.png"
              alt="PSG IAS"
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                objectFit: "cover",
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
              <Text strong style={{ color: "#fff", fontSize: 16 }}>
                PSG IAS Command Center
              </Text>
              <Text style={{ color: "#cfcfcf", fontSize: 12 }}>Alumni Admin Panel</Text>
            </div>
          </div>
        </Col>

        {/* Center: Navigation */}
        <Col flex="auto" style={{ display: "flex", justifyContent: "center" }}>
          <Menu
            mode="horizontal"
            selectedKeys={[activeKey]}
            style={{
              background: "transparent",
              borderBottom: "none",
              fontWeight: 600,
              fontSize: 15,
              color: "#fff",
            }}
          >
            <Menu.Item
              key="dashboard"
              icon={<HomeOutlined />}
              onClick={() => navigate("/admin/dashboard")}
              style={{ color: "#fff" }}
            >
              Dashboard
            </Menu.Item>

            <Menu.Item
              key="insights"
              icon={<BarChartOutlined />}
              onClick={() => navigate("/admin/insights")}
              style={{ color: "#fff" }}
            >
              Insights
            </Menu.Item>

            <Menu.Item
              key="events"
              icon={<CalendarOutlined />}
              onClick={() => navigate("/admin/events")}
              style={{ color: "#fff" }}
            >
              Events
            </Menu.Item>

            <Menu.Item
              key="jobs"
              icon={<SolutionOutlined />}
              onClick={() => navigate("/admin/jobs")}
              style={{ color: "#fff" }}
            >
              Jobs
            </Menu.Item>

            <Menu.Item
              key="approvals"
              icon={<CheckCircleOutlined />}
              onClick={() => navigate("/admin/approvals")}
              style={{ color: "#fff" }}
            >
              Approvals
            </Menu.Item>

            {/* 🆕 New Alumni List Menu Item */}
            <Menu.Item
              key="alumni-list"
              icon={<TeamOutlined />}
              onClick={() => navigate("/admin/alumni-list")}
              style={{ color: "#fff" }}
            >
              Alumni List
            </Menu.Item>

            <Menu.Item
              key="settings"
              icon={<SettingOutlined />}
              onClick={() => navigate("/admin/settings")}
              style={{ color: "#fff" }}
            >
              Settings
            </Menu.Item>
          </Menu>
        </Col>


        {/* Right: user dropdown */}
        <Col flex="none">
          <Dropdown overlay={userMenu} placement="bottomRight" arrow overlayStyle={{ boxShadow: "0 6px 18px rgba(0,0,0,0.12)" }}>
            <Space style={{ cursor: "pointer", gap: 10 }}>
              <Avatar
                size="large"
                style={{ backgroundColor: "#0D47FF", verticalAlign: "middle" }}
                aria-label="admin-avatar"
              >
                {initials || <UserOutlined />}
              </Avatar>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <Text strong style={{ color: "#fff", lineHeight: 1 }}>
                  {adminName}
                </Text>
                <Text style={{ color: "#bdbdbd", fontSize: 12, lineHeight: 1 }}>Administrator</Text>
              </div>
            </Space>
          </Dropdown>
        </Col>
      </Row>
    </Header>
  );
}
